// @flow
import isPlainObject from 'lodash/isPlainObject'
import escapeRegExp from 'lodash/escapeRegExp'
import toLower from 'lodash/toLower'
import forEach from 'lodash/forEach'

// Constants
import {
  SENSITIVE_FIELDS,
  REDACTED,
  REDACTED_BODY,
  SENSITIVE_REQUEST_HEADERS,
  SENSITIVE_RESPONSE_HEADERS,
} from './constants'

const isLikelyJsonString = (str) => {
  if (typeof str !== 'string') {
    return false
  }

  const s = str.trim()

  if (!s) {
    return false
  }

  const first = s[0]
  const last = s[s.length - 1]

  return (first === '{' && last === '}') || (first === '[' && last === ']')
}

const buildSensitiveRegex = (fields) => {
  return new RegExp(`(${fields.map(escapeRegExp).join('|')})`, 'i')
}

export function sanitizeObject(input, opts) {
  const options = Object.assign(
    { sensitiveFields: SENSITIVE_FIELDS, redacted: REDACTED, maxDepth: 8, maxArrayLength: 1000 },
    opts || {}
  )
  const sensitiveRe = buildSensitiveRegex(options.sensitiveFields)
  const seen = new WeakSet()

  function redactDeep(value, depth) {
    if (value == null || typeof value !== 'object') {
      return value
    }

    if (Array.isArray(value)) {
      if (seen.has(value)) {
        return '[Circular]'
      }

      seen.add(value)
      const out = []
      const len = Math.min(value.length, options.maxArrayLength)

      for (let i = 0; i < len; i++) {
        out[i] = redactDeep(value[i], depth + 1)
      }

      if (value.length > len) {
        out.push('...[truncated]')
      }

      return out
    }

    if (!isPlainObject(value)) {
      return value
    }

    if (seen.has(value)) {
      return '[Circular]'
    }

    seen.add(value)

    if (depth >= options.maxDepth) {
      return '[truncated]'
    }

    const out = {}

    for (const key of Object.keys(value)) {
      if (sensitiveRe.test(toLower(key))) {
        out[key] = options.redacted
      } else {
        out[key] = redactDeep(value[key], depth + 1)
      }
    }

    return out
  }

  return redactDeep(input, 0)
}

export function sanitizeBody(body, opts) {
  const sensitiveRe = buildSensitiveRegex((opts && opts.sensitiveFields) || SENSITIVE_FIELDS)

  if (typeof body === 'string') {
    if (isLikelyJsonString(body)) {
      try {
        return sanitizeObject(JSON.parse(body), opts)
      } catch (e) {
        console.error(e)
        return REDACTED_BODY
      }
    }
    return sensitiveRe.test(body) ? REDACTED_BODY : body
  }

  const tag = Object.prototype.toString.call(body)
  if (
    tag === '[object FormData]' ||
    tag === '[object Blob]' ||
    tag === '[object ArrayBuffer]' ||
    tag === '[object ReadableStream]' ||
    tag === '[object URLSearchParams]'
  ) {
    return body
  }

  return sanitizeObject(body, opts)
}

export function removeSensitiveHeaders(headers, sensitiveList) {
  const sens = (sensitiveList || SENSITIVE_REQUEST_HEADERS).map(toLower)
  const out = {}

  if (headers && typeof headers.forEach === 'function') {
    headers.forEach((value, key) => {
      if (!sens.includes(toLower(String(key)))) {
        out[key] = value
      }
    })

    return out
  }

  const obj = headers || {}

  forEach(obj, (value, key) => {
    if (!sens.includes(toLower(key))) {
      out[key] = value
    }
  })

  return out
}

export function requestSanitizer(request) {
  return {
    method: request && request.method,
    url: request && request.url,
    headers: removeSensitiveHeaders(request && request.headers, SENSITIVE_REQUEST_HEADERS),
    body: sanitizeBody(request && request.body),
  }
}

export function responseSanitizer(response) {
  return {
    status: response && (response.status || response.statusCode),
    headers: removeSensitiveHeaders(response && response.headers, SENSITIVE_RESPONSE_HEADERS),
    body: sanitizeBody(response && response.body),
  }
}
